import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ApiError } from "@core/http";
import { setPasswordWithToken } from "./api";
import { Shell, BackButton } from "./AccessLayout";
import { BigButton, Field, Notice } from "./primitives";
import "./access.css";

// SPEC-301 §6 · `validador.jsx:192-232` (`fuerza`, `NIVELES`, `NuevaClave`).
//
// El mínimo (8) sale del propio schema del contrato
// (`api/contracts/parts/50-validator.yaml:60`, `password: minLength 8`): no
// hay endpoint que exponga `security.password_min_length`. Mismo precedente
// que `web/src/portal/access/NewPasswordScreen.tsx:13-16`.
const MIN_LENGTH = 8;

function passwordStrength(password: string): number {
  let score = 0;
  if (password.length >= MIN_LENGTH) score++;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  return Math.min(score, 4);
}

// `NIVELES` (`validador.jsx:201-203`). "Aceptable" usa el hex literal del
// prototipo (design-reviewer, wave 1, lo marcó `fail`: "los valores
// literales son innegociables"). DESIGN-TOKENS.md no define un token CSS
// para ese tercer nivel intermedio (solo documenta `--danger`/`validator-
// Danger`, ver `web/src/core/theme/tokens.ts`, y reutiliza `--gold`/`--gold-
// 2` en los extremos "Buena"/"Muy segura"): no hay token existente que
// sustituya este valor. Declararlo un token nuevo (p. ej. `--gold-mid`)
// vive en `web/src/core/theme/**`, fuera de `writes:` de este oficio
// (`web-validator` no escribe `core/**`, ver rol). Queda documentado en el
// reporte como incertidumbre para que el humano decida si vale una baseline
// nueva y un token de `web-core`, en vez de forzar la corrección acá.
const STRENGTH_LEVELS = [
  { label: "Muy débil", color: "var(--danger)" },
  { label: "Débil", color: "var(--danger)" },
  { label: "Aceptable", color: "#D6C08A" },
  { label: "Buena", color: "var(--gold)" },
  { label: "Muy segura", color: "var(--gold-2)" },
] as const;

export function NewPasswordScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const strength = passwordStrength(password);
  const level = STRENGTH_LEVELS[password ? strength : 0] ?? STRENGTH_LEVELS[0];
  const matches = confirmation.length > 0 && password === confirmation;
  const ready = password.length >= MIN_LENGTH && matches;

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!ready) return;
    setError(null);
    setSubmitting(true);
    try {
      await setPasswordWithToken(token, password, confirmation);
      navigate("/validator/access-login", { replace: true });
    } catch (err) {
      const status = err instanceof ApiError ? err.status : null;
      const messageByStatus: Record<number, string> = {
        401: "El enlace no es válido o ya venció. Solicita uno nuevo.",
        400: "Las contraseñas no coinciden.",
      };
      setError(
        (status !== null && messageByStatus[status]) ||
          "No se pudo establecer la contraseña. Intenta de nuevo.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Shell>
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="sc-screen sc-screen--compact"
      >
        <BackButton
          onClick={() => {
            navigate("/validator/access-login");
          }}
        />
        <h1 className="sc-title">Nueva contraseña</h1>
        <p className="sc-copy">
          Use al menos {MIN_LENGTH} caracteres, con mayúsculas, números y un
          símbolo.
        </p>
        {error ? <Notice>{error}</Notice> : null}
        <Field
          label="Nueva contraseña"
          kind="password"
          value={password}
          onChange={setPassword}
          placeholder="••••••••"
        />
        <div style={{ margin: "-4px 0 20px" }}>
          <div className="sc-strength-track">
            {[0, 1, 2, 3].map((i) => (
              <span
                key={i}
                className="sc-strength-seg"
                style={
                  password && i < strength
                    ? { background: level.color }
                    : undefined
                }
              />
            ))}
          </div>
          <div
            className="sc-strength-label"
            style={password ? { color: level.color } : undefined}
          >
            {password
              ? `Seguridad: ${level.label}`
              : "Seguridad de la contraseña"}
          </div>
        </div>
        <Field
          label="Repetir contraseña"
          kind="password"
          value={confirmation}
          onChange={setConfirmation}
          placeholder="••••••••"
          error={confirmation.length > 0 && !matches}
        />
        {confirmation.length > 0 && !matches ? (
          <div className="sc-error-text">Las contraseñas no coinciden.</div>
        ) : null}
        <BigButton type="submit" disabled={!ready || submitting}>
          {submitting ? "Guardando…" : "Guardar y entrar"}
        </BigButton>
        <div className="sc-flex-1" />
      </form>
    </Shell>
  );
}
